import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Grid, Card, CardActionArea, CardContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import { API_BASES, VSS_CONFIG } from '../../../config/constants';

export default function VoiceCallDialog({ open, onClose, vehicle, token }) {
  const [activeMode, setActiveMode] = useState(null);

  const deviceId = vehicle?.integration_imei || vehicle?.raw?.integration_imei || vehicle?.info?.integration_imei || '';

  // IP could be extracted from IP_URL or we can use the domain host if the IP is unknown.
  // We'll extract the IP from IP_URL if possible, fallback to VSS_CONFIG.HOST
  let serverIp = VSS_CONFIG.IP_URL.match(/\/\/([0-9.]+)/);
  serverIp = serverIp ? serverIp[1] : VSS_CONFIG.HOST;

  const webIntercomUrl = `${API_BASES.VSS}/apiPage/dialog.html?token=${token}&deviceId=${deviceId}&serverIp=${serverIp}`;
  const realListenUrl = `${API_BASES.VSS}/apiPage/RealListen.html?token=${token}&deviceId=${deviceId}&chs=1`;

  const handleOpenIntercom = () => setActiveMode('intercom');
  const handleOpenRealListen = () => setActiveMode('reallisten');

  const handleClose = () => {
    setActiveMode(null);
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={activeMode ? 'md' : 'sm'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(8, 14, 30, 0.15)',
          background: '#ffffff',
          overflow: 'hidden',
          minHeight: activeMode ? '70vh' : 'auto'
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeMode && (
            <IconButton
              onClick={() => setActiveMode(null)}
              sx={{ color: '#1a237e', mr: 1, bgcolor: 'rgba(26, 35, 126, 0.04)', '&:hover': { bgcolor: 'rgba(26, 35, 126, 0.08)' } }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a237e' }}>
              {activeMode === 'intercom' ? 'Web Intercom' : activeMode === 'reallisten' ? 'Real Listen' : 'Voice Communication'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontWeight: 500 }}>
              {vehicle?.fleet_name || 'Vehicle'} {activeMode ? '' : '- Select communication mode'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: '#485D9A',
            bgcolor: 'rgba(72, 93, 154, 0.08)',
            '&:hover': { bgcolor: 'rgba(72, 93, 154, 0.15)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 1, display: 'flex', flexDirection: 'column', height: activeMode ? '100%' : 'auto' }}>
        {!activeMode ? (
          <>
            <Grid container spacing={2}>
              {/* Web Intercom Card */}
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: '#0374DA',
                      boxShadow: '0 8px 24px rgba(3, 116, 218, 0.15)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardActionArea onClick={handleOpenIntercom} sx={{ height: '100%', p: 2 }}>
                    <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          bgcolor: 'rgba(3, 116, 218, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          color: '#0374DA',
                          transition: 'transform 0.2s',
                          '&:hover': { transform: 'scale(1.05)' }
                        }}
                      >
                        <RecordVoiceOverIcon sx={{ fontSize: 32 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                        Web Intercom
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Two-way real-time voice call with the driver.
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>

              {/* Real Listen Card */}
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: '#10b981',
                      boxShadow: '0 8px 24px rgba(16, 185, 129, 0.15)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardActionArea onClick={handleOpenRealListen} sx={{ height: '100%', p: 2 }}>
                    <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          bgcolor: 'rgba(16, 185, 129, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          color: '#10b981',
                          transition: 'transform 0.2s',
                          '&:hover': { transform: 'scale(1.05)' }
                        }}
                      >
                        <HeadsetMicIcon sx={{ fontSize: 32 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                        Real Listen
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Monitor real-time audio conditions inside the vehicle cabin.
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(241, 245, 249, 0.7)', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', lineHeight: 1.5 }}>
                <strong>Information:</strong> If the Web Intercom screen is blank or displays a <em>"404 Not Found"</em> message, the
                endpoint URL (<code>/vss/apiPage/dialog.html</code>) is currently unavailable on the Howen VSS server.
              </Typography>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              width: '100%',
              height: '100%',
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              bgcolor: '#f1f5f9',
              position: 'relative'
            }}
          >
            {/* Visualizer Underneath */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#ffffff',
                zIndex: 10
              }}
            >
              <style>
                {`
                  .audio-wave {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    height: 100px;
                  }
                  .audio-bar {
                    width: 12px;
                    background-color: ${activeMode === 'intercom' ? '#0374DA' : '#10b981'};
                    border-radius: 6px;
                    animation: wave 1.2s ease-in-out infinite;
                  }
                  .audio-bar:nth-of-type(1) { animation-delay: 0.0s; }
                  .audio-bar:nth-of-type(2) { animation-delay: 0.2s; }
                  .audio-bar:nth-of-type(3) { animation-delay: 0.4s; }
                  .audio-bar:nth-of-type(4) { animation-delay: 0.6s; }
                  .audio-bar:nth-of-type(5) { animation-delay: 0.8s; }
                  .audio-bar:nth-of-type(6) { animation-delay: 1.0s; }
                  .audio-bar:nth-of-type(7) { animation-delay: 0.5s; }

                  @keyframes wave {
                    0%, 100% { height: 20px; }
                    50% { height: 80px; }
                  }
                `}
              </style>
              <div className="audio-wave">
                <div className="audio-bar" />
                <div className="audio-bar" />
                <div className="audio-bar" />
                <div className="audio-bar" />
                <div className="audio-bar" />
                <div className="audio-bar" />
                <div className="audio-bar" />
              </div>
              <Typography variant="h6" sx={{ mt: 4, fontWeight: 700, color: activeMode === 'intercom' ? '#0374DA' : '#10b981' }}>
                {activeMode === 'intercom' ? 'Connecting Intercom...' : 'Monitoring Audio...'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                Tap this area if the audio doesn't play automatically.
              </Typography>
            </Box>

            <iframe
              src={activeMode === 'intercom' ? webIntercomUrl : realListenUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="microphone; autoplay"
              title={activeMode === 'intercom' ? 'Web Intercom' : 'Real Listen'}
              style={{
                display: 'block',
                minHeight: '500px',
                position: 'relative',
                zIndex: 20,
                opacity: activeMode === 'intercom' ? 1 : 0.01
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
